import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './components/admin/AuthContext';
import Hero from './components/Hero';
import Navbar from './components/Navbar';
import Blog from './pages/Blog';
import ArticuloDetalle from './pages/blog/ArticuloDetalle';
import './App.css';

// CRITICAL: Lazy load ALL non-critical components
const ScrollInteractiveSection = lazy(() => import('./components/ScrollInteractiveSection'));
const MobileFloatingShapes = lazy(() => import('./components/MobileFloatingShapes'));
const Services = lazy(() => import('./components/Services'));
const SectionTransition = lazy(() => import('./components/SectionTransition'));
const PricingPlans = lazy(() => import('./components/PricingPlans'));
const PricingTransition = lazy(() => import('./components/PricingTransition'));
const Footer = lazy(() => import('./components/Footer'));
const ModernEffects = lazy(() => import('./components/ModernEffects').then(module => ({ 
  default: () => (
    <>
      <module.FloatingParticles />
      <module.MouseFollower />
      <module.GradientOrbs />
      <module.ScrollProgress />
      <module.FloatingActionButton />
    </>
  )
})));
const GeometricEffects = lazy(() => import('./components/GeometricEffects'));
const SubscriptionPlans = lazy(() => import('./components/SubscriptionPlans'));
const Process = lazy(() => import('./components/Process'));
const ProjectExamples = lazy(() => import('./components/ProjectExamples'));
const FAQ = lazy(() => import('./components/FAQ'));
const Contact = lazy(() => import('./components/Contact'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const InaugurationDiscount = lazy(() => import('./components/InaugurationDiscount'));
const Checkout = lazy(() => import('./components/Checkout'));
const OrderConfirmation = lazy(() => import('./components/OrderConfirmation'));
const NotFound404 = lazy(() => import('./components/NotFound404'));
const Forbidden403 = lazy(() => import('./components/Forbidden403'));

// Admin Components (lazy loaded - solo carga cuando se accede)
const Login = lazy(() => import('./components/admin/Login'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'));

// Legal Pages (lazy loaded)
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

// Content Pages (lazy loaded)
const SobreNosotros = lazy(() => import('./pages/SobreNosotros'));
const ServiciosPage = lazy(() => import('./pages/ServiciosPage'));
const ComoTrabajamos = lazy(() => import('./pages/ComoTrabajamos'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/30 to-slate-900">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-purple-400 mb-4"></div>
      <p className="text-slate-300 text-lg">Cargando...</p>
    </div>
  </div>
);

function App() {
  const [cookiesAccepted, setCookiesAccepted] = useState(false);

  useEffect(() => {
    const accepted = localStorage.getItem('cookiesAccepted');
    if (accepted === 'true') {
      setCookiesAccepted(true);
    }
    
    // PERFORMANCE: Remove preload class immediately on mobile, small delay on desktop
    document.body.classList.add('preload');
    
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // Mobile: remove immediately to show content
      setTimeout(() => {
        document.body.classList.remove('preload');
      }, 50);
    } else {
      // Desktop: wait for load event
      window.addEventListener('load', () => {
        setTimeout(() => {
          document.body.classList.remove('preload');
        }, 100);
      });
    }
  }, []);

  const handleCookieAccept = () => {
    setCookiesAccepted(true);
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('analyticsEnabled', 'true');
  };

  const handleCookieReject = () => {
    setCookiesAccepted(true);
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('analyticsEnabled', 'false');
  };

  // Wrapper component to check route
  const AppContent = () => {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const [shouldLoadEffects, setShouldLoadEffects] = useState(false);
    const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

    useEffect(() => {
      if (isAdminRoute || isMobile) return; // NO cargar en admin ni móvil
      
      // Desktop: Cargar efectos visuales después de 1s
      const timer = setTimeout(() => {
        setShouldLoadEffects(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }, [isAdminRoute, isMobile]);

    return (
      <>
        {/* Navbar - NO mostrar en rutas admin */}
        {!isAdminRoute && <Navbar />}
        
        {/* Modern Effects - SOLO DESKTOP */}
        {!isAdminRoute && !isMobile && shouldLoadEffects && (
          <Suspense fallback={null}>
            <ModernEffects />
            <GeometricEffects />
          </Suspense>
        )}
        
        {/* Routes */}
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Main website */}
            <Route path="/" element={<HomePage />} />
            
            {/* Content pages */}
            <Route path="/sobre-nosotros" element={<SobreNosotros />} />
            <Route path="/servicios" element={<ServiciosPage />} />
            <Route path="/como-trabajamos" element={<ComoTrabajamos />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<ArticuloDetalle />} />
            
            {/* Legal pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            
            {/* Checkout page */}
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            
            {/* Admin routes */}
            <Route path="/admin" element={<Login />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Error pages */}
            <Route path="/403" element={<Forbidden403 />} />
            <Route path="*" element={<NotFound404 />} />
          </Routes>
        </Suspense>
        
        {/* Cookie Banner */}
        {!cookiesAccepted && !isAdminRoute && (
          <Suspense fallback={null}>
            <CookieBanner onAccept={handleCookieAccept} onReject={handleCookieReject} />
          </Suspense>
        )}
        
        {/* Inauguration Discount Modal */}
        {!isAdminRoute && (
          <Suspense fallback={null}>
            <InaugurationDiscount />
          </Suspense>
        )}
      </>
    );
  };

  // Main Home Page Component
  const HomePage = () => {
    const [isMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    const [shouldLoad3D, setShouldLoad3D] = useState(false);

    useEffect(() => {
      if (!isMobile) {
        // Desktop: cargar componentes 3D normalmente
        setShouldLoad3D(true);
      }
      // Móvil: NO cargar nunca componentes 3D (Three.js)
    }, [isMobile]);

    return (
      <div className="relative z-10">
        <Hero />
        
        {/* Efectos visuales - Desktop: 3D Three.js | Mobile: CSS shapes */}
        {isMobile ? (
          <Suspense fallback={null}>
            <MobileFloatingShapes />
          </Suspense>
        ) : shouldLoad3D ? (
          <Suspense fallback={null}>
            <ScrollInteractiveSection />
          </Suspense>
        ) : null}
        
        {/* Servicios - Lazy loaded */}
        <Suspense fallback={null}>
          <Services />
        </Suspense>
        
        {/* Transition - Lazy loaded */}
        <Suspense fallback={null}>
          <SectionTransition />
        </Suspense>
        
        {/* Pricing - Lazy loaded */}
        <Suspense fallback={null}>
          <PricingPlans />
        </Suspense>
        
        {/* Transition - Lazy loaded */}
        <Suspense fallback={null}>
          <PricingTransition />
        </Suspense>
        
        {/* Below-fold components - Lazy loaded */}
        <Suspense fallback={<div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div></div>}>
          <SubscriptionPlans />
          <Process />
          <ProjectExamples />
          <FAQ />
          <Contact />
        </Suspense>
        
        {/* Footer - Lazy loaded */}
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </div>
    );
  };

  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <div className="App">
            <AppContent />
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;